import React, { useState, useRef, useEffect } from 'react'
import styles from './styles/questionConversationWindow.module.scss'
import { QuestionConversationWindowProps, Typing } from '../constants/types'
import classNames from 'classnames'
import Icon, { IconType } from '@bt-react/components/Icon'
import Button from '@bt-react/components/Button'
import QuestionItemReply from './QuestionItemReply'
import { QuestionLoadingState } from '../constants/enums'

const QuestionConversationWindow = ({ hideConversationWindow, hideReplyOption, handleCloseClick, question, typing,
  sendMessageLoading, showAnsweredNotification, setHideReplyOption, setAllMessagesAsRead,
  addReplyToConversation, handleKeyDownEvent, resetSendQuestionLoadingState }: QuestionConversationWindowProps) => {
  const [ showReplies, setShowReplies ] = useState(true)
  const [ showUnreadNotification, setShowUnreadNotification ] = useState(false)
  const [ showNoTextEnteredError, setShowNoTextEnteredError ] = useState(false)
  const targetRef = useRef<HTMLLIElement | null>(null)
  const inputRefReply = useRef(null)
  const buttonsRef = useRef(null)

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (showNoTextEnteredError && inputRefReply.current.value !== '') setShowNoTextEnteredError(false)
    handleKeyDownEvent?.(event, question.uniqueConversationName, inputRefReply.current.value, question.isAnswered)
  }

  const handleSendReply = async () => {
    if (inputRefReply.current.value === '') return setShowNoTextEnteredError(true)
    const result = await addReplyToConversation(inputRefReply.current.value, question.uniqueConversationName ? question.uniqueConversationName : '', question.isAnswered)
    setHideReplyOption(result)
  }

  useEffect(() => {

    if (question.numOfMessagesRead < question.numberOfRepliesToTheQuestion) {
      setShowUnreadNotification(true)

      // IntersectionObserver to set all messages as read when the user scrolls the last message into view
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setAllMessagesAsRead(question.uniqueConversationName, question.messages.length - 1)
            setShowUnreadNotification(false)
          }
        },
        { threshold: 0.8 } // 80% of the target element visible
      )

      const refCurrent = targetRef.current

      if (refCurrent) {
        observer.observe(refCurrent)
      }

      return () => {
        if (refCurrent) {
          observer.unobserve(refCurrent)
        }
      }

    }

  }, [question.numOfMessagesRead, question.numberOfRepliesToTheQuestion, question.uniqueConversationName, setAllMessagesAsRead, question.messages])

  useEffect(() => {
    const buttonElement = buttonsRef.current

    if (buttonElement) buttonElement.scrollIntoView({ block: "end" })

  }, [hideReplyOption, hideConversationWindow])

  return <>
    <div className={classNames(styles['question-list__question-conversation-window-background'], !hideConversationWindow && styles['question-list__question-conversation-window-background--open'])}></div>
    <div
      data-bdd='question-conversation-window'
      className={classNames(styles['question-list__question-conversation-window'],
      !hideConversationWindow && styles['question-list__question-conversation-window--open'])}>
      <div className={styles['question-list__question-conversation-window__heading']}>
        <h4>Reply</h4>
        <div
          data-bdd='close-widow-btn'
          className={styles['question-list__question-conversation-window__close-btn']}
          onClick={handleCloseClick}
        >
          Close
          <Icon className={styles['question-list__question__question_reply-btn-icon']} name={IconType.iconName.close} />
        </div>
        <br />
        {showAnsweredNotification &&
          <p data-bdd='question-answered-notification' className={styles['question-list__question__question_answered-notification']}>
            <Icon className={styles['question-list__question__question_answered-icon']} name={IconType.iconName.tick} />
            Question has been marked as answered
          </p>
        }
      </div>
      <div className={styles['question-list__question-conversation-window__replies']}>
        <QuestionItemReply
          key={question.id}
          question={{
            user: {
              name: question.user?.name ? question.user.name : '',
              companyName: question.user?.companyName ? question.user.companyName : ''
            },
            created: question.created,
            uniqueConversationName: question.uniqueConversationName ? question.uniqueConversationName : '',
            text: question.text ? question.text : ''
          }}
        />
        {question.messages.length > 1 &&
          <button
            data-bdd={'show-replies-btn'}
            className={styles['question-list__question__question-reply-btn']}
            onClick={() => setShowReplies(!showReplies)}>
            <Icon
              className={styles['question-list__question__question_reply-btn-icon']}
              name={showReplies ? IconType.iconName.caretUp : IconType.iconName.caretDown} />
            { question.messages.length > 2 ? `${question.messages.length - 1} replies` : `1 reply` }
            { showUnreadNotification && <span className={styles['question-list__question__question_unread-replies']} /> }
          </button>
        }
        {showReplies &&
          <ul
            data-bdd={'replies-list'}
            className={styles['question-list__question-conversation-window__question-replies-list']}>
            {
              question.messages.map((message, index) => index !== 0 &&
                <li
                  key={message.messageId}
                  className={styles['question-list__question-conversation-window__question-replies-list__list-item']}
                  data-bdd='question-reply'
                  ref={question.numOfMessagesRead < question.numberOfRepliesToTheQuestion && question.messages.length - 1 ? targetRef : null}>
                  <QuestionItemReply
                    question={{
                      user: {
                        name: question.user?.authorExternalIdV2 === message.messageUserId ? question.user.name :
                          message.messageAuthor ? message.messageAuthor : '',
                        companyName:  question.user?.authorExternalIdV2 === message.messageUserId ? question.user.companyName :
                          message.messageCompanyName ? message.messageCompanyName : ''
                      },
                      created: message.created,
                      uniqueConversationName: question.uniqueConversationName ? question.uniqueConversationName : '',
                      text: message.messageBody ? message.messageBody : ''
                    }}
                  />
                </li>
              )
            }
          </ul>
        }
      </div>
      <button
        data-bdd='show-reply-option-btn'
        className={styles['question-list__question__question-reply-btn']}
        onClick={() => {
          resetSendQuestionLoadingState()
          setHideReplyOption(false)
        }}
        disabled={sendMessageLoading !== QuestionLoadingState.SUCCESS && !hideReplyOption}>
        <Icon className={styles['question-list__question__question_reply-btn-icon']} name={IconType.iconName.reply} /> Reply
      </button>
      <div className={styles['question-list__question-conversation-window__typing-indicator']}>
        {
          typing && typing.length > 0 && typing.map((obj: Typing) =>
            obj.conversationName === question.uniqueConversationName &&<p key={obj.conversationName}><span>{obj.authorName}</span> is typing...</p>)
        }
      </div>
      {
        sendMessageLoading !== QuestionLoadingState.SUCCESS && !hideReplyOption &&
        <>
          <textarea
            className={showNoTextEnteredError && styles['question-list__question-conversation-window__textarea-error-border']}
            data-bdd='question-textarea'
            placeholder={'Type your reply here...'}
            onKeyDown={(e) => handleKeyPress(e)}
            ref={inputRefReply} />
          <div className={styles['question-list__question-conversation-window__action-btns']}>
            <Button
              dataBdd='cancel-reply-btn'
              color={IconType.colour.secondary}
              className={styles['question-list__question-conversation-window__cancel-btn']}
              onClick={() => setHideReplyOption(true)}>
                Cancel
            </Button>
            <Button
              dataBdd='add-reply-btn'
              color={IconType.colour.primary}
              disabled={!inputRefReply}
              className={styles['question-list__question-conversation-window__reply-btn']}
              loading={sendMessageLoading === QuestionLoadingState.LOADING}
              onClick={handleSendReply}>
               <span ref={buttonsRef}>Reply</span>
            </Button>
          </div>
          {sendMessageLoading === QuestionLoadingState.ERROR &&
            <p
              data-bdd='send-message-error'
              className={styles['question-list__question-conversation-window__send-message-error']}>
                There was an error sending your message, please try again
            </p>
          }
        </>
      }
    </div>
  </>
}
export default QuestionConversationWindow